import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../../stores/useAuthStore';

/**
 * OTP Verification Screen — shown after registration form submission.
 * 6-digit code input with auto-focus, resend timer, and branded styling.
 */
export default function OtpVerification() {
  const { pendingEmail, verifyOtp, resendOtp, loading, error, clearError } = useAuthStore();
  const navigate = useNavigate();
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [resendTimer, setResendTimer] = useState(60);
  const [success, setSuccess] = useState(false);
  const inputRefs = useRef([]);

  // Countdown timer for resend
  useEffect(() => {
    if (resendTimer <= 0) return;
    const id = setInterval(() => setResendTimer((t) => t - 1), 1000);
    return () => clearInterval(id);
  }, [resendTimer]);

  // Auto-focus first input
  useEffect(() => {
    if (inputRefs.current[0]) inputRefs.current[0].focus();
  }, []);

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // digits only
    clearError();
    const next = [...digits];
    next[index] = value.slice(-1); // single digit
    setDigits(next);

    // Auto-advance to next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits filled
    if (value && index === 5) {
      const otp = [...next].join('');
      if (otp.length === 6) handleVerify(otp);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const next = [...digits];
    pasted.split('').forEach((d, i) => { next[i] = d; });
    setDigits(next);
    if (pasted.length === 6) handleVerify(pasted);
  };

  const handleVerify = async (otp) => {
    console.log('[OTP] Verifying code for:', pendingEmail, 'otp:', otp);
    try {
      const result = await verifyOtp(pendingEmail, otp);
      console.log('[OTP] Verification success! User:', result.email, 'Role:', result.role);
      setSuccess(true);
      // Redirect to role-based dashboard
      const target = result.role === 'SPEAKER' ? '/dashboard/speaker'
        : result.role === 'ATTENDEE' ? '/dashboard/attendee'
        : result.role === 'ADMIN' ? '/admin' : '/';
      console.log('[OTP] Redirecting to:', target);
      setTimeout(() => navigate(target), 1500);
    } catch (err) {
      console.error('[OTP] Verification failed:', err.message);
    }
  };

  const handleResend = async () => {
    console.log('[OTP] Resending OTP to:', pendingEmail);
    try {
      await resendOtp(pendingEmail);
      setResendTimer(60);
      setDigits(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } catch { /* error is set in store */ }
  };

  if (success) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.successIcon}>✓</div>
          <h2 style={styles.title}>Email Verified!</h2>
          <p style={styles.subtitle}>Redirecting to your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.eyebrow}>VERIFY YOUR EMAIL</div>
        <h2 style={styles.title}>Enter Verification Code</h2>
        <p style={styles.subtitle}>
          We've sent a 6-digit code to<br />
          <strong style={{ color: '#C9A96E' }}>{pendingEmail}</strong>
        </p>

        {/* OTP Inputs */}
        <div style={styles.otpRow} onPaste={handlePaste}>
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => (inputRefs.current[i] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={d}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              style={{
                ...styles.otpInput,
                borderColor: d ? '#C9A96E' : 'rgba(255,255,255,0.1)',
                color: d ? '#C9A96E' : '#fff',
              }}
              aria-label={`Digit ${i + 1}`}
            />
          ))}
        </div>

        {error && <div style={styles.error}>{error}</div>}

        <button
          style={{
            ...styles.verifyBtn,
            opacity: loading || digits.some((d) => !d) ? 0.5 : 1,
          }}
          disabled={loading || digits.some((d) => !d)}
          onClick={() => handleVerify(digits.join(''))}
        >
          {loading ? 'Verifying...' : 'Verify & Create Account'}
        </button>

        <div style={styles.resendRow}>
          <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>
            Didn't receive the code?
          </span>
          {resendTimer > 0 ? (
            <span style={{ color: 'rgba(201,169,110,0.6)', fontSize: 13 }}>
              Resend in {resendTimer}s
            </span>
          ) : (
            <button style={styles.resendBtn} onClick={handleResend} disabled={loading}>
              Resend Code
            </button>
          )}
        </div>

        <p style={styles.hint}>
          Check your spam folder if you don't see the email.
        </p>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '120px 20px 40px',
    background: '#050d1a',
  },
  card: {
    width: '100%',
    maxWidth: 460,
    padding: '48px clamp(20px, 5vw, 40px)',
    border: '1px solid rgba(201,169,110,0.15)',
    background: 'rgba(10,22,40,0.8)',
    textAlign: 'center',
  },
  eyebrow: {
    fontFamily: "'Syncopate',sans-serif",
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: '0.3em',
    color: '#C9A96E',
    marginBottom: 16,
  },
  title: {
    fontFamily: "'Cormorant Garamond',serif",
    fontSize: 32,
    fontWeight: 300,
    color: '#fff',
    marginBottom: 12,
  },
  subtitle: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.5)',
    lineHeight: 1.6,
    marginBottom: 36,
  },
  otpRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: 'clamp(6px, 2vw, 10px)',
    marginBottom: 28,
    flexWrap: 'wrap',
  },
  otpInput: {
    width: 'clamp(40px, 10vw, 52px)',
    height: 'clamp(48px, 12vw, 60px)',
    background: '#0A1628',
    border: '2px solid rgba(255,255,255,0.1)',
    textAlign: 'center',
    fontSize: 'clamp(18px, 5vw, 24px)',
    fontWeight: 700,
    fontFamily: "'Courier New',monospace",
    letterSpacing: 2,
    outline: 'none',
    transition: 'border-color 0.2s',
    borderRadius: 0,
  },
  error: {
    padding: '10px 16px',
    background: 'rgba(239,68,68,0.1)',
    border: '1px solid rgba(239,68,68,0.3)',
    color: '#ef4444',
    fontSize: 13,
    marginBottom: 20,
  },
  verifyBtn: {
    width: '100%',
    padding: '16px',
    background: '#C9A96E',
    color: '#050d1a',
    border: 'none',
    fontFamily: "'Jost',sans-serif",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    transition: 'opacity 0.2s',
    marginBottom: 24,
  },
  resendRow: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  resendBtn: {
    background: 'none',
    border: 'none',
    color: '#C9A96E',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    textDecoration: 'underline',
    fontFamily: "'Jost',sans-serif",
  },
  hint: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.25)',
    marginTop: 8,
  },
  successIcon: {
    width: 64,
    height: 64,
    border: '2px solid #7BC67E',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
    fontSize: 28,
    color: '#7BC67E',
  },
};
